// =============================================================================
// features/sales/campaigns/operate/components/LeadSidePanel.tsx — Painel
// lateral: histórico recente + canais disponíveis (doc 26 §6.8 "Painel
// lateral reúne histórico recente e canais disponíveis").
//
// Reusa os MESMOS componentes/hooks do módulo comercial (F34-S04/S05) — não
// duplica a leitura de preferências nem o cálculo de "bloqueado":
//   - `useContactPreferences` (hooks/sales/useContactPreferences.ts);
//   - `ContactRestrictionBanner` (features/sales/contacts, texto sempre
//     presente — não só cor, briefing do slot #3);
//   - `ContactAttemptsHistory` (mesmo componente do CRM).
// =============================================================================

import type { ContactChannel } from '@elemento/shared-schemas';
import { CONTACT_CHANNELS } from '@elemento/shared-schemas';
import * as React from 'react';

import { useContactPreferences } from '../../../../../hooks/sales/useContactPreferences';
import { ContactAttemptsHistory } from '../../../contacts/ContactAttemptsHistory';
import { ContactRestrictionBanner } from '../../../contacts/ContactRestrictionBanner';
import { CHANNEL_LABELS } from '../../../contacts/labels';

export interface LeadSidePanelProps {
  leadId: string;
}

/**
 * Exposto para `OperationFooter` (via `useContactPreferences` direto lá
 * também, mesmo hook — TanStack Query deduplica a requisição) decidir se o
 * botão "Registrar resultado" fica desabilitado quando TODOS os canais estão
 * bloqueados.
 */
export function LeadSidePanel({ leadId }: LeadSidePanelProps): React.JSX.Element {
  const { blockedChannels, blocked, isFullyBlocked, isLoading } = useContactPreferences(leadId);

  const availableChannels = CONTACT_CHANNELS.filter((c: ContactChannel) => !blockedChannels.has(c));

  return (
    <div className="flex flex-col gap-6">
      {!isLoading && blocked.length > 0 && (
        <ContactRestrictionBanner blocked={blocked} isFullyBlocked={isFullyBlocked} />
      )}

      <div
        className="flex flex-col gap-3 rounded-md border border-border p-4"
        style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-1)' }}
      >
        <p className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3">
          Canais disponíveis
        </p>
        {availableChannels.length === 0 ? (
          <p className="font-sans text-sm text-ink-3">Nenhum canal disponível para este lead.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {availableChannels.map((channel) => (
              <li key={channel} className="font-sans text-sm text-ink-2 flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-pill shrink-0"
                  style={{ background: 'var(--success)' }}
                />
                {CHANNEL_LABELS[channel]}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ContactAttemptsHistory leadId={leadId} />
    </div>
  );
}
